import { Component, computed, inject, signal } from '@angular/core';
import { HorizonRatingsFacade } from '@app/modules/cop/store/horizon-ratings';
import { CategoryRanksForOverview, HorizonRatingsUtilsService, RatingsCategoryRanks, getCountryCode } from '../horizon-ratings-utils';
import { RatingsDocumentTreeNode } from './ratings-document-tree-node.model';
import { TranslateService } from '@ngx-translate/core';

const populateDescendantNodes = (
  treeNode: RatingsDocumentTreeNode,
  _map: Record<string, RatingsDocumentTreeNode>
): RatingsDocumentTreeNode => {
  treeNode.children.forEach(childNode => {
    const entry = _map[childNode.document.horizon_category_id];

    if (!!entry) {
      childNode.children.push(...entry.children);

      if (entry.children.length) {
        populateDescendantNodes(entry, _map);
      }
    }
  });

  return treeNode;
};

@Component({
  selector: 'topo-ratings-expanded-view',
  templateUrl: './ratings-expanded-view.component.html',
  styleUrls: ['./ratings-expanded-view.component.scss'],
  standalone: false
})
export class RatingsExpandedViewComponent {
  protected facade = inject(HorizonRatingsFacade);
  protected translate = inject(TranslateService);
  private ratingUtils = inject(HorizonRatingsUtilsService);

  protected selectedTabIndex = signal(0);

  protected showContent = signal(false);

  documentTree = computed(() => {
    const documentsArr = this.facade.ratingsDocuments();

    const categoryDescendantMap = documentsArr.reduce((acc, document) => {
      if (acc.hasOwnProperty(document.horizon_category_id)) {
        acc[document.horizon_category_id].document = document;
      } else {
        acc[document.horizon_category_id] = {
          document,
          children: [],
          id: document.horizon_category_id
        };
      }

      const parentId = document?.horizon_parent_id ?? 'root';

      if (acc.hasOwnProperty(parentId)) {
        acc[parentId].children = [...acc[parentId].children, { document, children: [], id: document.horizon_category_id }].sort(
          (a, b) => a.document.rank - b.document.rank
        );
      } else {
        acc[parentId] = {
          document: null,
          children: [{ document, children: [], id: document.horizon_category_id }],
          id: parentId
        };
      }

      return acc;
    }, {});

    const tree = populateDescendantNodes(categoryDescendantMap?.root ?? { children: [] }, categoryDescendantMap);

    return tree;
  });

  categoryTabs = computed(() =>
    this.documentTree()?.children?.map(({ document }) => ({
      label: document.category_tag,
      rank: document.rank
    }))
  );

  selectedDocumentNode = computed(() => {
    const index = this.selectedTabIndex();
    const tree = this.documentTree();
    return tree?.children?.length ? tree.children[index] : null;
  });

  selectedNodeRatingText = computed(() => {
    const node = this.selectedDocumentNode();
    if (!node) {
      return;
    }

    return this.ratingUtils.getRatingsTextAndColor(node.document.custom_rating_number ?? node.document.rating_number)?.text;
  });

  overviewNode = computed(() => {
    const tree = this.documentTree();
    return tree?.children?.find(node => node.document.rank === RatingsCategoryRanks.Overview);
  });

  topLevelCategoryNodes = computed(() => {
    const tree = this.documentTree();
    return tree?.children
      ?.filter(node => node.document.rank !== RatingsCategoryRanks.Overview)
      .filter(node => CategoryRanksForOverview.includes(node.document.rank));
  });

  flagBannerImgSrc = computed(async () => {
    const selectedOverviewDocument = this.facade.selectedOverviewDocument();

    if (!Boolean(selectedOverviewDocument)) {
      return null;
    }

    const { country_name, language_code, country_id } = selectedOverviewDocument;
    const countryCode = await getCountryCode(country_id, country_name, language_code);
    return `https://files.topo.ai/flag_banners/flag-banner_${countryCode.toUpperCase()}.png`;
  });

  handleTabChanged(tabIndex: number): void {
    this.selectedTabIndex.set(tabIndex);
  }

  hideExpandedView(): void {
    this.facade.hideExpandedView();
    this.selectedTabIndex.set(0);
  }

  onAnimationFinish(show: boolean): void {
    this.showContent.set(show);
  }
}
